import { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { SmoothNum } from "./primitives";

/* LIVE P&L — broker-truth day P&L (realized since the CME day rolled at 18:00 ET + open), per-position rows. */
const usd = (n) => `${n < 0 ? "-" : n > 0 ? "+" : ""}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tone = (n) => (n > 0 ? "text-[var(--r2-emerald)]" : n < 0 ? "text-[var(--r2-red)]" : "text-[var(--r2-text1)]");
const tag = "ml-1 rounded-sm border px-1 text-[6.5px] font-bold tracking-[0.12em]";

/* Two-tap adopt: first tap arms, second tap hands the user's lots to the PME (ATR stop -> BE / trail / TP ladder). */
const AdoptButton = ({ sym }) => {
  const [arm, setArm] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.stopPropagation();
    if (!arm) { setArm(true); setTimeout(() => setArm(false), 6000); return; }
    setBusy(true);
    try {
      const r = await api.pmeAdopt(sym);
      toast(r.ok ? `${sym} adopted — stop ${r.stop} (${r.atr_mult}× ${r.atr_source} from ${r.anchor}); PME now runs BE / trail / TP ladder`
                 : `Adopt failed — ${r.error}`);
    } catch (err) { toast(`Adopt failed — ${err?.response?.data?.detail || err.message}`); }
    finally { setBusy(false); setArm(false); }
  };
  return (
    <button onClick={go} disabled={busy} data-testid={`pnl-adopt-${sym}`}
      className={`${tag} py-[1px] transition-colors ${arm ? "border-[var(--r2-red)] bg-[var(--r2-red)]/15 text-[var(--r2-red)]" : "border-[var(--r2-cyan)] text-[var(--r2-cyan)] hover:bg-[var(--r2-cyan)]/10"}`}
      title="Hand this position to the Position Management Engine: 1.2× 1H-ATR stop from your avg entry, then break-even, trail and TP ladder">
      {busy ? "ADOPTING…" : arm ? "TAP AGAIN TO CONFIRM" : "ADOPT INTO PME"}
    </button>
  );
};

export const LivePnl = ({ account = {}, positions = [], selected, onSelect }) => {
  const day = account.daily_pnl ?? 0, open = account.unrealized_pnl ?? 0, real = account.realized_pnl ?? 0;
  return (
    <div className="flex flex-col gap-2" data-testid="live-pnl">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[8px] font-mono uppercase tracking-[0.22em] text-[var(--r2-text2)]">Day P&amp;L · {account.live ? "LIVE ACCOUNT" : "PAPER"}</div>
          <SmoothNum value={day} format={usd} className={`block font-mono text-[22px] font-black leading-none r2-num tabular-nums ${tone(day)}`} testid="pnl-day" />
        </div>
        <div className="grid grid-cols-2 gap-x-4 text-right">
          <div><div className="text-[7.5px] font-mono uppercase tracking-[0.18em] text-[var(--r2-text2)]">Open</div><SmoothNum value={open} format={usd} className={`font-mono text-[11px] font-semibold tabular-nums ${tone(open)}`} testid="pnl-open" /></div>
          <div><div className="text-[7.5px] font-mono uppercase tracking-[0.18em] text-[var(--r2-text2)]">Realized</div><SmoothNum value={real} format={usd} className={`font-mono text-[11px] font-semibold tabular-nums ${tone(real)}`} testid="pnl-realized" /></div>
        </div>
      </div>
      <div className="flex justify-between text-[8.5px] font-mono text-[var(--r2-text2)]">
        <span>CASH <span className="text-[var(--r2-text1)]" data-testid="pnl-cash">${(account.balance ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span></span>
        <span>EQUITY <span className="text-[var(--r2-text1)]" data-testid="pnl-equity">${(account.equity ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span></span>
        {account.agent_day_pnl != null && <span>AGENT <span className={tone(account.agent_day_pnl)}>{usd(account.agent_day_pnl)}</span></span>}
      </div>
      {positions.length > 0 && (
        <ul className="divide-y divide-[var(--r2-border)] border-t border-[var(--r2-border)]" data-testid="pnl-positions">
          {positions.map((p) => {
            const sym = p.root || p.symbol, sel = sym === selected;
            return (
              <li key={p.id || sym} data-testid={`pnl-pos-${sym}`}>
                <div onClick={() => onSelect?.(sym)} role="button" tabIndex={0} className={`w-full grid grid-cols-[auto_1fr_auto] items-center gap-2 py-1.5 text-left cursor-pointer ${sel ? "" : "opacity-85 hover:opacity-100"}`} data-testid={`pnl-pos-btn-${sym}`}>
                  <span className="font-mono text-[10px] font-bold text-[var(--r2-text1)]">{sym} <span className={p.direction === "LONG" ? "text-[var(--r2-emerald)]" : "text-[var(--r2-red)]"}>{p.direction}</span> ×{p.qty}
                    {p.manual ? (<>
                        <span className={`${tag} border-[var(--r2-amber)] text-[var(--r2-amber)]`} data-testid={`pnl-pos-manual-${sym}`}>MANUAL · NOT MANAGED</span>
                        {p.live && <AdoptButton sym={sym} />}
                      </>)
                      : p.adopted ? <span className={`${tag} border-[var(--r2-cyan)] text-[var(--r2-cyan)]`} data-testid={`pnl-pos-adopted-${sym}`}>ADOPTED · PME{p.pme_state ? ` · ${p.pme_state}` : ""}</span>
                      : p.manual_qty > 0 ? <span className={`${tag} border-[var(--r2-cyan)] text-[var(--r2-cyan)]`} data-testid={`pnl-pos-shared-${sym}`}>AGENT ×{p.agent_qty} · MANUAL ×{p.manual_qty}</span> : null}</span>
                  <span className="font-mono text-[8.5px] text-[var(--r2-text2)] truncate">{p.entry_price} → {p.current_price}{p.stop_loss ? ` · SL ${p.stop_loss}` : " · NO STOP"}{p.take_profit ? ` · TP ${p.take_profit}` : ""}</span>
                  <SmoothNum value={p.pnl ?? 0} format={usd} className={`font-mono text-[11px] font-bold tabular-nums ${tone(p.pnl ?? 0)}`} testid={`pnl-pos-val-${sym}`} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {!positions.length && <div className="text-[8.5px] font-mono text-[var(--r2-text2)]">flat — no open positions</div>}
    </div>
  );
};
